// pages/reports/DueInfoReport.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Card, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, alpha, Tooltip, IconButton, Fade,
} from '@mui/material';
import {
  FileDownload as ExcelIcon, PictureAsPdf as PdfIcon, Refresh as RefreshIcon,
  ExpandMore as ExpandIcon, ExpandLess as CollapseIcon, Analytics as Icon,
  KeyboardArrowUp, KeyboardArrowDown,
} from '@mui/icons-material';

import ReportFilterBar from '../../components/reports/ReportFilterBar';
import { reportService, DueGroup } from '../../services/reportService';
import { tradesService } from '../../services/tradesService';
import { useAuth } from '../../contexts/AuthContext';
import { fmt2, fmtDate } from '../../components/reports/ReportLayout';

const ACCENT = '#667EEA';

// ─────────────────────────────────────────────────────────────
// Permission Helper
// ─────────────────────────────────────────────────────────────
const useIsDataManager = () => {
  const { user } = useAuth();
  return user?.is_superuser || user?.role?.role_name === 'DATA_MANAGER';
};

// ─────────────────────────────────────────────────────────────
// Months & Years
// ─────────────────────────────────────────────────────────────
const MONTHS = [
  { value: '1', label: 'January' }, { value: '2', label: 'February' },
  { value: '3', label: 'March' }, { value: '4', label: 'April' },
  { value: '5', label: 'May' }, { value: '6', label: 'June' },
  { value: '7', label: 'July' }, { value: '8', label: 'August' },
  { value: '9', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const RANKS = [
  { value: 'A', label: 'A' }, { value: 'B', label: 'B' },
  { value: 'C', label: 'C' }, { value: 'D', label: 'D' },
  { value: 'N', label: 'N' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => ({
  value: String(currentYear - i),
  label: String(currentYear - i),
}));

// ─────────────────────────────────────────────────────────────
// Styled Header Cell
// ─────────────────────────────────────────────────────────────
const HCell: React.FC<{ label: string; align?: 'left' | 'right' | 'center'; first?: boolean; last?: boolean }> = ({
  label,
  align = 'right',
  first,
  last,
}) => (
  <TableCell
    sx={{
      fontWeight: 700,
      fontSize: '0.72rem',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: '#fff',
      py: 1.4,
      px: 1.5,
      whiteSpace: 'nowrap',
      textAlign: align,
      position: 'sticky',
      top: 0,
      zIndex: 3,
      background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
      borderRight: last ? 'none' : '1px solid rgba(255,255,255,0.15)',
      borderBottom: 'none',
      ...(first && { borderTopLeftRadius: 0 }),
      ...(last && { borderTopRightRadius: 0 }),
    }}
  >
    {label}
  </TableCell>
);

// ─────────────────────────────────────────────────────────────
// Body Cell Helper
// ─────────────────────────────────────────────────────────────
const cellSx = (align: 'left' | 'right' | 'center' = 'left', extra?: object) => ({
  py: 1.1,
  px: 1.5,
  fontSize: '0.8rem',
  textAlign: align,
  borderBottom: `1px solid ${alpha(ACCENT, 0.07)}`,
  borderRight: `1px solid ${alpha(ACCENT, 0.05)}`,
  ...extra,
});

// ═════════════════════════════════════════════════════════════
// Due Information Report
// ═════════════════════════════════════════════════════════════
const DueInfoReport: React.FC = () => {
  const isDataManager = useIsDataManager();

  // ───────────────────────────────────────────────────────────
  // Data & Filter State
  // ───────────────────────────────────────────────────────────
  const [due, setDue] = useState<Record<string, DueGroup>>({});
  const [grand, setGrand] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [dlLoading, setDlLoading] = useState<'excel' | 'pdf' | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [products, setProducts] = useState<any[]>([]);
  const [sellers, setSellers] = useState<any[]>([]);

  const [filters, setFilters] = useState<Record<string, string>>({
    year: '',
    month: '',
    date_from: '',
    date_to: '',
    product_id: '',
    seller_info_id: '',
    seller_rank: '',
  });

  const sellerOptions = filters.seller_rank
  ? sellers.filter(s => s.rank === filters.seller_rank)
  : sellers;

  // ───────────────────────────────────────────────────────────
  // Vertical Scroll Arrow Rail
  // ───────────────────────────────────────────────────────────
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const holdDelay = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const [scroll, setScroll] = useState({ up: false, down: false, overflow: false });

  const updateScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const overflow = el.scrollHeight - el.clientHeight > 2;
    setScroll({
      overflow,
      up: el.scrollTop > 2,
      down: overflow && el.scrollTop < el.scrollHeight - el.clientHeight - 2,
    });
  }, []);

  const scrollByStep = (dy: number, smooth = true) => {
    scrollRef.current?.scrollBy({ top: dy, behavior: smooth ? 'smooth' : 'auto' });
  };

  const stopHold = useCallback(() => {
    if (holdDelay.current) {
      clearTimeout(holdDelay.current);
      holdDelay.current = null;
    }
    if (holdTimer.current) {
      clearInterval(holdTimer.current);
      holdTimer.current = null;
    }
  }, []);

  const startHold = (dir: 1 | -1) => {
    stopHold();
    scrollByStep(dir * 140);
    holdDelay.current = setTimeout(() => {
      holdTimer.current = setInterval(() => {
        scrollByStep(dir * 40, false);
      }, 30);
    }, 300);
  };

  const arrowKey = (dir: 1 | -1) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      scrollByStep(dir * 160);
    }
  };

  const arrowBtnSx = {
    borderRadius: 0,
    color: '#ee5a24',
    '&:hover': { background: alpha('#ee5a24', 0.12) },
    '&.Mui-disabled': { color: alpha('#ee5a24', 0.25) },
  } as const;

  // ───────────────────────────────────────────────────────────
  // Product / Seller Lists
  // ───────────────────────────────────────────────────────────
  useEffect(() => {
    tradesService.listProducts({ is_active: true })
      .then(r => setProducts(r.results.map(p => ({ value: p.product_id, label: p.product_name }))))
      .catch(() => {});

    tradesService.listSellers()
      .then(r => setSellers(r.results.map(s => ({ value: s.seller_info_id, label: s.seller_name, rank: s.seller_rank, }))))
      .catch(() => {});
  }, []);

  // ───────────────────────────────────────────────────────────
  // Fetch Due Report
  // ───────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p: any = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v) p[k] = v;
      });

      const res = await reportService.getDueReport(p);
      setDue(res.due || {});
      setGrand(res.grand_total || {});

      const exp: Record<string, boolean> = {};
      Object.keys(res.due || {}).forEach(k => {
        exp[k] = false;
      });
      setExpanded(exp);
    } catch {
      // Keep existing behaviour
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ───────────────────────────────────────────────────────────
  // Recalculate scroll arrows whenever data/expansion/size changes
  // ───────────────────────────────────────────────────────────
  useEffect(() => {
    updateScroll();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScroll);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateScroll, due, loading, expanded]);

  useEffect(() => () => stopHold(), [stopHold]);

  // ───────────────────────────────────────────────────────────
  // Download
  // ───────────────────────────────────────────────────────────
  const handleDownload = async (fmt: 'excel' | 'pdf') => {
    setDlLoading(fmt);
    try {
      const p: any = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v) p[k] = v;
      });
      await reportService.downloadDue(fmt, p);
    } finally {
      setDlLoading(null);
    }
  };

  // ───────────────────────────────────────────────────────────
  // Expand Seller & Sort
  // ───────────────────────────────────────────────────────────
  const toggle = (name: string) => setExpanded(prev => ({ ...prev, [name]: !prev[name] }));
  const sellerEntries = Object.entries(due).sort(([a], [b]) => a.localeCompare(b));

  // ───────────────────────────────────────────────────────────
  // Summary Cards
  // ───────────────────────────────────────────────────────────
  const cards = [
    { label: 'Total Sellers', value: sellerEntries.length, color: ACCENT },
    { label: 'Total Quantity', value: grand.total_pcs ?? 0, color: '#2196F3' },
    { label: 'Total Purchases (Tk.)', value: fmt2(grand.total_purchase ?? 0), color: '#00BCD4' },
    { label: 'Total Sales (Tk.)', value: fmt2(grand.total_sales ?? 0), color: '#4CAF50' },
    { label: 'Total Cash (Tk.)', value: fmt2(grand.total_cash ?? 0), color: '#FF9800' },
    { label: 'Total Due (Tk.)', value: fmt2(grand.total_due ?? 0), color: '#f44336' },
  ];

  // ═══════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════
  return (
    <Fade in timeout={400}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        
        {/* HEADER */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Due Report
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.25}>
              Due information grouped by seller and product
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchData}
                size="small"
                sx={{ border: `1px solid ${alpha(ACCENT, 0.3)}`, color: ACCENT }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {isDataManager && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={dlLoading === 'excel' ? <CircularProgress size={14} /> : <ExcelIcon />}
                  onClick={() => handleDownload('excel')}
                  disabled={!!dlLoading || loading}
                  sx={{ borderRadius: 2, borderColor: '#4CAF50', color: '#4CAF50', '&:hover': { background: alpha('#4CAF50', 0.06) } }}
                >
                  Excel
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={dlLoading === 'pdf' ? <CircularProgress size={14} /> : <PdfIcon />}
                  onClick={() => handleDownload('pdf')}
                  disabled={!!dlLoading || loading}
                  sx={{ borderRadius: 2, borderColor: '#f44336', color: '#f44336', '&:hover': { background: alpha('#f44336', 0.06) } }}
                >
                  PDF
                </Button>
              </>
            )}
          </Stack>
        </Box>

        {/* SUMMARY CARDS */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
          {cards.map(c => (
            <Card
              key={c.label}
              elevation={0}
              sx={{
                flex: 1,
                minWidth: 130,
                p: 1.75,
                borderRadius: 3,
                textAlign: 'center',
                border: `1px solid ${alpha(c.color, 0.2)}`,
                background: `linear-gradient(135deg, ${alpha(c.color, 0.07)} 0%, ${alpha(c.color, 0.02)} 100%)`,
                transition: 'transform 0.15s, box-shadow 0.15s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 20px ${alpha(c.color, 0.18)}` },
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ letterSpacing: '0.03em' }}>
                {c.label}
              </Typography>
              <Typography fontWeight={800} sx={{ color: c.color, lineHeight: 1.2, fontSize: '0.95rem', mt: 0.25, textAlign: 'center' }}>
                {c.value}
              </Typography>
            </Card>
          ))}
        </Stack>

        {/* FILTERS */}
        <Card elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${alpha(ACCENT, 0.1)}` }}>
          <ReportFilterBar
            accentColor={ACCENT}
            values={filters}
            onChange={(k, v) => setFilters(prev => {
              const next = { ...prev, [k]: v };
              if (k === 'seller_rank' && v && prev.seller_info_id) {
                const sel = sellers.find(s => String(s.value) === String(prev.seller_info_id));
                if (sel && sel.rank !== v) next.seller_info_id = '';
              }
              return next;
            })}
            onClear={() => setFilters({ year: '', month: '', date_from: '', date_to: '', product_id: '', seller_info_id: '', seller_rank: '' })}
            fields={[
              { type: 'select', key: 'year', label: 'Year', options: YEARS, minWidth: 100 },
              { type: 'select', key: 'month', label: 'Month', options: MONTHS, minWidth: 100 },
              { type: 'date', key: 'date_from', label: 'From' },
              { type: 'date', key: 'date_to', label: 'To' },
              { type: 'select', key: 'product_id', label: 'Product', options: products, minWidth: 200 },
              { type: 'select', key: 'seller_info_id', label: 'Seller', options: sellers, minWidth: 180 },
              { type: 'select', key: 'seller_rank',    label: 'Rank',    options: RANKS,         minWidth: 90 },
            ]}
          />
        </Card>

        {/* DUE TABLE CARD */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha(ACCENT, 0.12)}`,
            overflow: 'hidden', // Keeps arrow rail inside card
          }}
        >
          {/* TOP BAR */}
          <Box sx={{ px: 2.5, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${alpha(ACCENT, 0.08)}`, background: alpha(ACCENT, 0.03) }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: '#ea7c66', letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
              Due Data
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {sellerEntries.length} seller{sellerEntries.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {/* TABLE + VERTICAL ARROW RAIL */}
          <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
            {/* SCROLLABLE DUE TABLE */}
            <TableContainer
              ref={scrollRef}
              onScroll={updateScroll}
              sx={{
                flex: 1,
                minWidth: 0,
                maxHeight: 'calc(100vh - 340px)',
                overflowX: 'auto',
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: 6, height: 6 },
                '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '10px' },
                '&::-webkit-scrollbar-thumb': { background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)', borderRadius: '10px' },
                '&::-webkit-scrollbar-thumb:hover': { background: 'linear-gradient(135deg, #ff5252 0%, #d84a1f 100%)' },
              }}
            >
              <Table size="small" stickyHeader sx={{ minWidth: 750, borderCollapse: 'separate', borderSpacing: 0 }}>
                {/* HEADER */}
                <TableHead>
                  <TableRow>
                    <HCell label="#" align="center" first />
                    <HCell label="Date" align="left" />
                    <HCell label="Seller / Product" align="left" />
                    <HCell label="Pcs" align="right" />
                    <HCell label="Total Sales (Tk.)" align="right" />
                    <HCell label="Cash (Tk.)" align="right" />
                    <HCell label="Due (Tk.)" align="right" last />
                  </TableRow>
                </TableHead>

                {/* BODY */}
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6, border: 'none' }}>
                        <CircularProgress size={32} sx={{ color: ACCENT }} />
                        <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                          Loading data…
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : sellerEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8, border: 'none' }}>
                        <Icon sx={{ fontSize: 44, color: 'text.disabled', display: 'block', mx: 'auto', mb: 1.5 }} />
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          No Due information found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {/* SELLER GROUPS */}
                      {sellerEntries.map(([sellerName, grp], si) => (
                        <React.Fragment key={sellerName}>
                          {/* SELLER HEADER */}
                          <TableRow
                            onClick={() => toggle(sellerName)}
                            sx={{
                              cursor: 'pointer',
                              background: `linear-gradient(90deg, ${alpha(ACCENT, 0.1)} 0%, ${alpha(ACCENT, 0.04)} 100%)`,
                              transition: 'background 0.15s',
                              '&:hover': { background: alpha(ACCENT, 0.14) },
                              '& td:first-of-type': { boxShadow: `inset 3px 0 0 ${ACCENT}` },
                            }}
                          >
                            <TableCell sx={{ ...cellSx('center'), fontWeight: 700, color: ACCENT, fontSize: '0.82rem', minWidth: 48 }}>
                              {si + 1}
                            </TableCell>
                            <TableCell sx={{ ...cellSx('left'), color: 'text.disabled', fontSize: '0.78rem', minWidth: 110 }}>—</TableCell>
                            <TableCell sx={{ ...cellSx('left'), fontWeight: 800, fontSize: '0.875rem', minWidth: 200 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 22, height: 22, borderRadius: '6px', background: alpha(ACCENT, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', color: ACCENT, flexShrink: 0 }}>
                                  {expanded[sellerName] ? <CollapseIcon sx={{ fontSize: 14 }} /> : <ExpandIcon sx={{ fontSize: 14 }} />}
                                </Box>
                                {sellerName}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ ...cellSx('right'), fontWeight: 700, color: '#2196F3', minWidth: 70 }}>
                              {grp.total_pcs}
                            </TableCell>
                            <TableCell sx={{ ...cellSx('right'), fontWeight: 700, color: '#4CAF50', minWidth: 130 }}>
                              {fmt2(grp.total_sales)}
                            </TableCell>
                            <TableCell sx={{ ...cellSx('right'), fontWeight: 700, color: '#FF9800', minWidth: 120 }}>
                              {fmt2(grp.total_cash)}
                            </TableCell>
                            <TableCell sx={{ ...cellSx('right'), fontWeight: 700, color: '#f44336', minWidth: 110, borderRight: 'none' }}>
                              {fmt2(grp.total_due)}
                            </TableCell>
                          </TableRow>

                          {/* PRODUCT ROWS */}
                          {expanded[sellerName] && grp.rows.map((r, i) => (
                            <TableRow
                              key={i}
                              sx={{
                                background: i % 2 === 0 ? '#ffffff' : alpha(ACCENT, 0.018),
                                transition: 'background 0.15s',
                                '&:hover': { background: alpha(ACCENT, 0.07), '& td': { color: 'text.primary' } },
                              }}
                            >
                              <TableCell sx={{ ...cellSx('center'), color: 'text.disabled', fontSize: '0.75rem' }}>{i + 1}</TableCell>
                              <TableCell sx={{ ...cellSx('left'), color: 'text.secondary', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                                {r.sale_date ? fmtDate(r.sale_date) : '—'}
                              </TableCell>
                              <TableCell sx={{ ...cellSx('left'), color: 'text.secondary', pl: 4 }}>{r.product_name}</TableCell>
                              <TableCell sx={{ ...cellSx('right'), fontWeight: 600, color: '#2196F3' }}>{r.quantity_pcs}</TableCell>
                              <TableCell sx={{ ...cellSx('right') }}>{fmt2(r.total_sales_price)}</TableCell>
                              <TableCell sx={{ ...cellSx('right'), color: '#FF9800' }}>{fmt2(r.cash_amount)}</TableCell>
                              <TableCell sx={{ ...cellSx('right'), color: '#f44336', borderRight: 'none' }}>{fmt2(r.due_amount)}</TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}

                      {/* GRAND TOTAL */}
                      <TableRow
                        sx={{
                          background: `linear-gradient(90deg, ${alpha(ACCENT, 0.14)} 0%, ${alpha(ACCENT, 0.06)} 100%)`,
                          '& td:first-of-type': { boxShadow: `inset 4px 0 0 ${ACCENT}` },
                        }}
                      >
                        <TableCell sx={{ ...cellSx('center') }} />
                        <TableCell sx={{ ...cellSx('left') }} />
                        <TableCell sx={{ ...cellSx('left'), fontWeight: 800, fontSize: '0.875rem', color: ACCENT, letterSpacing: '0.04em' }}>
                          GRAND TOTAL
                        </TableCell>
                        <TableCell sx={{ ...cellSx('right'), fontWeight: 800, color: '#2196F3' }}>
                          {grand.total_pcs ?? 0}
                        </TableCell>
                        <TableCell sx={{ ...cellSx('right'), fontWeight: 800, color: '#4CAF50' }}>{fmt2(grand.total_sales ?? 0)}</TableCell>
                        <TableCell sx={{ ...cellSx('right'), fontWeight: 800, color: '#FF9800' }}>{fmt2(grand.total_cash ?? 0)}</TableCell>
                        <TableCell sx={{ ...cellSx('right'), fontWeight: 800, color: '#f44336', borderRight: 'none' }}>
                          {fmt2(grand.total_due ?? 0)}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* VERTICAL ARROW RAIL */}
            {scroll.overflow && (
              <Stack sx={{ width: 30, flexShrink: 0, borderLeft: `1px solid ${alpha('#ee5a24', 0.15)}`, background: alpha('#ee5a24', 0.035) }}>
                <Tooltip title="Scroll up" placement="left">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!scroll.up}
                      aria-label="Scroll up"
                      onKeyDown={arrowKey(-1)}
                      onMouseDown={() => startHold(-1)}
                      onMouseUp={stopHold}
                      onMouseLeave={stopHold}
                      onTouchStart={() => startHold(-1)}
                      onTouchEnd={stopHold}
                      sx={arrowBtnSx}
                    >
                      <KeyboardArrowUp fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Box sx={{ flex: 1 }} /> {/* Push down arrow to bottom */}

                <Tooltip title="Scroll down" placement="left">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!scroll.down}
                      aria-label="Scroll down"
                      onKeyDown={arrowKey(1)}
                      onMouseDown={() => startHold(1)}
                      onMouseUp={stopHold}
                      onMouseLeave={stopHold}
                      onTouchStart={() => startHold(1)}
                      onTouchEnd={stopHold}
                      sx={arrowBtnSx}
                    >
                      <KeyboardArrowDown fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )}
          </Box>
        </Card>
      </Box>
    </Fade>
  );
};

export default DueInfoReport;